import { z } from 'zod';

/**
 * Flow record tracker — one status per record (account or contact), not per
 * rep. `starred` / `snoozed` / `reminder` are mutually exclusive values of a
 * single field (see docs/flow/custom-views-notes.md#tracker); there is no
 * separate star flag. Absence of a `TrackerRecord` means "no tracker" —
 * `flow_record_trackers` has no row rather than a null-status row (Decision
 * 9: clearing a tracker deletes the row).
 *
 * Terra-only: this state does not sync to Salesforce.
 */
export const TrackerStatusSchema = z.enum(['starred', 'snoozed', 'reminder']);
export type TrackerStatus = z.infer<typeof TrackerStatusSchema>;

/** The two entities a tracker row can key off (`flow_record_trackers.entity`). */
export const TrackerEntitySchema = z.enum(['contact', 'account']);
export type TrackerEntity = z.infer<typeof TrackerEntitySchema>;

/**
 * True when `y`-`m`-`d` (1-indexed month) is a real calendar day. JS `Date`
 * normalizes out-of-range components instead of rejecting them (`2026-02-31`
 * silently becomes `2026-03-03`), so the standard round-trip trick is the
 * only reliable check: construct the date and verify it reports back the
 * exact same year/month/day.
 */
function isRealCalendarDate(y: number, m: number, d: number): boolean {
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
}

/**
 * A calendar date, `YYYY-MM-DD`. `flow_record_trackers.tracker_date` is a
 * postgres DATE (no time, no zone) and the list query SELECTs it back with
 * `to_char(...)`, so the wire value is a plain calendar day at both ends — a
 * timestamp here would silently shift the "due today" boundary by a timezone.
 *
 * The regex alone accepts calendar-invalid strings (`2026-02-31`,
 * `2026-13-01`): JS `Date` NORMALIZES rather than rejects those (`2026-02-31`
 * → `2026-03-03`), so a stored date could silently differ from what was
 * requested, and an out-of-range month can 500 during Prisma serialization.
 * The refinement below rejects anything that doesn't round-trip to the exact
 * same Y/M/D (R4).
 */
const CalendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be a YYYY-MM-DD calendar date')
  .refine(
    (value) => {
      const [y, m, d] = value.split('-').map(Number);
      return isRealCalendarDate(y, m, d);
    },
    { message: 'must be a real calendar date' },
  );

/**
 * The date rule from the doc's tracker matrix, shared by the wire record and
 * the PUT body: a dated status (`snoozed`/`reminder`) REQUIRES a date, and
 * `starred` must not carry one (a starred row has nothing to be due).
 */
function satisfiesDateRule(status: TrackerStatus, date: string | null): boolean {
  return status === 'starred' ? date === null : date !== null;
}

const DATE_RULE_MESSAGE =
  "trackerDate is required for 'snoozed'/'reminder' statuses and must be null for 'starred'";

/**
 * Wire shape for an existing tracker row.
 *
 * `trackerDate` is required (non-null) for `snoozed`/`reminder` and forced
 * null for `starred` — enforced by the refinement below, matching the API
 * validation matrix in the doc.
 */
export const TrackerRecordSchema = z
  .object({
    entity: TrackerEntitySchema,
    recordId: z.string(),
    trackerStatus: TrackerStatusSchema,
    /** ISO date string. Required for snoozed/reminder; always null for starred. */
    trackerDate: z.string().nullable(),
    note: z.string().nullable(),
    /** Stringified `User.id` of whoever last set this tracker. */
    updatedBy: z.string(),
    updatedAt: z.string(),
  })
  .refine(
    (record) => satisfiesDateRule(record.trackerStatus, record.trackerDate),
    { message: DATE_RULE_MESSAGE, path: ['trackerDate'] },
  );
export type TrackerRecord = z.infer<typeof TrackerRecordSchema>;

/**
 * `PUT /api/flow/tracker/:entity/:recordId` body.
 *
 * `entity`/`recordId` ride the path, `updatedBy` is the ACTING user (never
 * client-supplied), so the body is just the tracker's own state. Omitted
 * `trackerDate`/`note` normalize to null — the route UPSERTS a whole tracker,
 * so a missing key means "no date"/"no note", not "leave the old one".
 *
 * The same date rule as `TrackerRecordSchema` applies, which is what makes
 * `starred` + a date a 400 rather than a server-side null (see the handler's
 * note): one rule, declared once, enforced identically on both boundaries.
 */
export const TrackerUpsertBodySchema = z
  .object({
    trackerStatus: TrackerStatusSchema,
    trackerDate: CalendarDateSchema.nullable().optional().transform(v => v ?? null),
    /** Free text ("why?"). Capped to keep junk out of an unbounded TEXT column. */
    note: z.string().trim().max(1000).nullable().optional().transform(v => (v ? v : null)),
  })
  .strict()
  .refine(
    (body) => satisfiesDateRule(body.trackerStatus, body.trackerDate),
    { message: DATE_RULE_MESSAGE, path: ['trackerDate'] },
  );
export type TrackerUpsertBody = z.infer<typeof TrackerUpsertBodySchema>;

// ── Buckets (groupBy 'tracker' + the Tracker view's auto-sort) ───────────────

/**
 * The tracker buckets, in their canonical display/sort order — the prototype's
 * `trackerBucket()` (docs/flow/custom-views-prototype.html:491) and Decision 10.
 * Index === rank, which is exactly what the server's `tracker_rank` CASE emits,
 * so the Tracker view's auto-sort and the group headers agree by construction.
 */
/**
 * Field key a view sets as `groupBy` to bucket rows by tracker state.
 * Canonical home for this constant — `tracker-sql.ts` (server) and
 * `FlowTable.tsx` (client Group picker, Unit 6a) both need it and must
 * agree on the literal, so it lives in this shared module rather than
 * being redeclared on either side.
 */
export const TRACKER_GROUP_KEY = 'tracker';

export const TRACKER_BUCKETS = ['Due', 'Starred', 'Reminders', 'Snoozed', 'No status'] as const;
export type TrackerBucket = (typeof TRACKER_BUCKETS)[number];

export const TRACKER_BUCKET_RANK: Record<TrackerBucket, number> = Object.fromEntries(
  TRACKER_BUCKETS.map((bucket, rank) => [bucket, rank]),
) as Record<TrackerBucket, number>;

/**
 * Bucket a record from its tracker state — the TypeScript twin of the server's
 * `TRACKER_BUCKET_SQL` (src/services/flow/query/tracker-sql.ts), which builds
 * its CASE from the same bucket list. Both are first-match-wins in the same
 * order; `tracker-sql.test.ts` pins the SQL against this order so the pair
 * can't drift silently.
 *
 * `today` is a `YYYY-MM-DD` calendar day (ISO date strings compare
 * lexicographically, so no Date parsing is involved on either side).
 */
export function trackerBucketOf(
  status: string | null | undefined,
  date: string | null | undefined,
  today: string,
): TrackerBucket {
  if (status === 'snoozed' || status === 'reminder') {
    if (date != null && date <= today) return 'Due';
    return status === 'reminder' ? 'Reminders' : 'Snoozed';
  }
  if (status === 'starred') return 'Starred';
  return 'No status';
}
